'use client';

import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

type Viewport = 'desktop' | 'mobile';

const ViewportContext = createContext<Viewport>('mobile');

export const DESKTOP_QUERY = '(min-width: 1024px)';

const VIEWPORT_COOKIE = 'shot_viewport';
const VIEWPORT_COOKIE_MAX_AGE = 14 * 24 * 60 * 60;

function writeViewportCookie(value: Viewport): void {
  if (typeof document === 'undefined') return;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${VIEWPORT_COOKIE}=${value}; Path=/; Max-Age=${VIEWPORT_COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
}

interface ViewportProviderProps {
  initial: Viewport;
  children: ReactNode;
}

export function ViewportProvider({ initial, children }: ViewportProviderProps) {
  const [viewport, setViewport] = useState<Viewport>(initial);
  const lastWrittenRef = useRef<Viewport | null>(null);

  useEffect(() => {
    const mql = window.matchMedia(DESKTOP_QUERY);
    const apply = (matches: boolean) => {
      const next: Viewport = matches ? 'desktop' : 'mobile';
      setViewport(next);
      if (lastWrittenRef.current !== next) {
        writeViewportCookie(next);
        lastWrittenRef.current = next;
      }
    };
    apply(mql.matches);
    const handler = (e: MediaQueryListEvent) => apply(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  return <ViewportContext.Provider value={viewport}>{children}</ViewportContext.Provider>;
}

export function useViewport(): Viewport {
  return useContext(ViewportContext);
}

export function useIsDesktop(): boolean {
  return useContext(ViewportContext) === 'desktop';
}
